// Instrument readings (CNT-1, ARCH §29.10). PURE (ENG-1).
//
// A reading is a USE ACTION processed at a tick boundary: given whatever the host
// already resolved from world state (an aimed cell's light, a farmland's moisture,
// the nearest ELT/aircraft, an aimed animal's genome), these functions compute the
// exact action-bar line plus the `instrument_read` trigger(s) to fire — never the
// world lookup itself, and never a mutation (§29.10's "no reading mutates world
// state" is enforced by construction: nothing here takes a mutable world).
//
// Five of the six fire exactly one trigger per use (`metric` names the one numeric
// field the binding format reports). The gene sequencer is the documented exception
// (§29.10/§29.12's per-gene objectives, e.g. "sequence a chicken with boldness ≥
// 200"): it fires one trigger per POLYGENIC gene in the genome, `metric` = that
// gene's name and `value` = its raw byte (0..255) — the value a `min`/`max` bound
// in a lesson criterion actually wants, not the (possibly inverted/rescaled)
// expressed trait. Mendelian genes have no single meaningful numeric value, so they
// contribute text only, never a trigger.
import { AVI } from '../config';
import { expressedAllele, geneValue } from '../heredity/express';
import type { Gene, MendelianGene } from '../heredity/genes';

/** One instrument reading: the action-bar line, and the `instrument_read`
 *  trigger(s) it fires (`metric`/`value` — the host supplies `instrument`). */
export interface Reading {
  readonly text: string;
  readonly triggers: ReadonlyArray<{ readonly metric: string; readonly value: number }>;
}

// ── light_meter (biology) ────────────────────────────────────────────────────

/** `world.getSkyLight`/`getBlockLight` at the aimed cell, else the player's feet
 *  (the host resolves which). Fires on `sky` — the field the binding format leads
 *  with. */
export function readLightMeter(sky: number, block: number): Reading {
  return { text: `Light: sky ${sky} · block ${block}`, triggers: [{ metric: 'sky', value: sky }] };
}

// ── soil_probe (biology) ─────────────────────────────────────────────────────

/** The aimed farmland's `moisture` (0..7) and, if a genetic crop stands above it,
 *  its `droughtTol` percent (else omitted from the line). Fires on `moisture`. */
export function readSoilProbe(moisture: number, droughtTolPercent: number | null): Reading {
  const text = droughtTolPercent === null
    ? `Soil: moisture ${moisture}/7`
    : `Soil: moisture ${moisture}/7 · drought tolerance ${droughtTolPercent}%`;
  return { text, triggers: [{ metric: 'moisture', value: moisture }] };
}

// ── signal_meter (ground-team) ───────────────────────────────────────────────

/** `sar.ts` `rssi` to the nearest in-range ELT, already resolved by the host
 *  (`null` beyond `SAR.ELT_RANGE`, or no beacon at all). Still fires one trigger
 *  out of range — `value` is a finite floor sentinel, not `-Infinity`, so a
 *  criterion bounding it never has to special-case an unfireable field. */
export function readSignalMeter(rssiDbm: number | null): Reading {
  if (rssiDbm === null) return { text: 'Signal: none', triggers: [{ metric: 'rssi', value: -999 }] };
  const n = Math.round(rssiDbm);
  return { text: `Signal: ${n} dBm`, triggers: [{ metric: 'rssi', value: n }] };
}

// ── altimeter / airspeed_indicator (aviation) ────────────────────────────────

/** Altitude above ground, in feet (Atlas/Earth) or blocks (elsewhere) — the host
 *  passes `agl` already in blocks and says which unit applies (§29.10). */
export function readAltimeter(aglBlocks: number, feet: boolean): Reading {
  const n = Math.round(feet ? aglBlocks * AVI.FEET_PER_BLOCK : aglBlocks);
  return { text: `Altitude: ${n} ${feet ? 'ft' : 'blocks'} AGL`, triggers: [{ metric: 'altitude', value: n }] };
}

/** Airspeed in knots — always converted (blocks/tick → m/s @ WORLD.TPS, 1 block = 1
 *  metre in Atlas/Earth's ENU frame → knots), matching the one binding format
 *  (§29.10 shows no other unit for this instrument). */
export function readAirspeed(speedBlocksPerTick: number): Reading {
  const mps = speedBlocksPerTick * AVI.TPS_FOR_SPEED;
  const kt = Math.round(mps * AVI.KNOTS_PER_MPS);
  return { text: `Airspeed: ${kt} kt`, triggers: [{ metric: 'airspeed', value: kt }] };
}

// ── gene_sequencer (biology) ──────────────────────────────────────────────────

const utf8Cap = (s: string): string => s.length ? s[0]!.toUpperCase() + s.slice(1) : s;

/** Both alleles a Mendelian gene's pair carries, as a two-letter code (`Ww`-style,
 *  §28.11) in the pair's stored order — mirrors the client notebook screen's own
 *  `genotypePair` (sim can't import client code, ENG-1; the one-liner is
 *  deliberately duplicated rather than shared across the wall). */
function genotypePair(gene: MendelianGene, a: number, b: number): string {
  return (gene.alleles[a] ?? '?') + (gene.alleles[b] ?? '?');
}

/** The aimed animal's or crop's genotype through §28's `express` + allele pairs:
 *  every Mendelian gene as its two-letter pair, every polygenic gene as its raw
 *  byte (the value a lesson's `min ≥ 200`-style bound actually reads) — one
 *  `instrument_read` trigger per POLYGENIC gene (§29.10's documented exception to
 *  "one reading, one trigger"). With the rule off, or no gene table/genome
 *  (aimed at a non-genetic entity), it reads the fixed "no genetics" line and
 *  fires nothing. */
export function readSequencer(
  species: string, id: number, genes: readonly Gene[] | undefined, genome: Uint8Array | undefined, heredityRule: boolean,
): Reading {
  if (!heredityRule || !genes || !genome) return { text: 'No genetics in this world.', triggers: [] };
  const parts: string[] = [];
  const triggers: Array<{ metric: string; value: number }> = [];
  let at = 0;
  for (const g of genes) {
    if (g.kind === 'mendelian') {
      const pair = genotypePair(g, genome[at]!, genome[at + 1]!);
      const allele = expressedAllele(g, genome[at]!, genome[at + 1]!);
      parts.push(`${g.name} ${pair} (${allele})`);
      at += 2;
    } else {
      const raw = genome[at]!;
      const value = geneValue(raw, g.expresses.range);
      parts.push(`${g.name} ${raw} (${value.toFixed(2)})`);
      triggers.push({ metric: g.name, value: raw });
      at += 1;
    }
  }
  return { text: `${utf8Cap(species)} #${id}: ${parts.join(' · ')}`, triggers };
}
